import { useCallback, useMemo } from "react";
import { useImmer } from "use-immer";
import { useEffect, useContext, useState } from "react";
import { ACTIVATE_DEACTIVATE_USER, APP_BASE_URL, DELETE_USER, GET_ALL_USER } from "../../Common/EndPoints";
import {
  Alert,
  Button,
  TextField,
  InputAdornment,
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Collapse,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import GroupIcon from "@mui/icons-material/Group";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "axios";
import DataTable from "../DataGrid/DataGrid";

import AddUser from "./AddUser";
import DeleteModal from "../../Modal/DeleteModal";
import Snackbar from "@mui/material/Snackbar";
import "./User.scss";
import { ActiveAlarmsData } from "../Alarms/ActiveAlarms";
import { USERS_COLUMNS } from "./Constants/UserColumns.jsx";
import { extractMasterRoleIds } from "../../Utils/Utils";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ActivateDeactivateUser from "../../Modal/ActivateDeactivateUser";

// Statistics Card Component
const StatCard = ({ title, value, icon, color, onClick, isActive }) => (
  <Card
    className={`stat-card ${isActive ? "stat-card--active" : ""}`}
    sx={{
      minWidth: 150,
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.2s ease",
      border: isActive ? `2px solid ${color}` : "1px solid #e0e0e0",
      "&:hover": onClick ? { transform: "translateY(-2px)", boxShadow: 3 } : {},
    }}
    onClick={onClick}
  >
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Box sx={{ color, display: "flex", alignItems: "center" }}>{icon}</Box>
      <Box>
        <Typography variant="h5" component="div" fontWeight="bold">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

// Export to CSV function
const exportToCSV = (data, filename = "users.csv") => {
  const headers = ["Name", "Email", "Role", "Service Type", "Status", "Stations", "Systems", "Mobile Number", "Created Time"];
  const csvContent = [
    headers.join(","),
    ...data.map((row) =>
      [
        `"${row.name}"`,
        `"${row.email}"`,
        `"${row.authority}"`,
        `"${row["G/NG"]}"`,
        row.isEnable ? "Active" : "Inactive",
        `"${row.stations?.join("; ") || ""}"`,
        `"${row.softwareResponses?.map((s) => s.code).join("; ") || ""}"`,
        `"${row.mobileNumber || ""}"`,
        row.createdTime ? new Date(row.createdTime).toLocaleString() : "",
      ].join(",")
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

const initialState = {
  userData: [],
  userActionLabel: "Create USER",
  showUserModal: false,
  showDeleteModal: false,
  id: null,
  selectedRow: null,
  loader: true,
  severityType: null,
  snackbarMessage: null,
  snackbar: false,
};
const addUser = (setState) => {
  setState((draft) => {
    draft.showUserModal = true;
  });
};
function getAllUsers({ params, signal }) {
  const url = APP_BASE_URL + GET_ALL_USER;
  return axios.get(url, { params, signal });
}

const User = () => {
  const navigate = useNavigate();
  const USER_ID = localStorage.getItem("userId");
  let roleHr = JSON.parse(localStorage.getItem('roleHierarchies'))
  let MASTER_ROLE_IDS = extractMasterRoleIds(roleHr)
  const [state, setState] = useImmer(initialState);
  const { newUserData, customerIdList } = useContext(ActiveAlarmsData);
  const [userStationsData, setUserStationsData] = useState([]);
  const access = window.frontendAccess?.USER;
  const allowCreateUser = access?.create ?? true;
  const allowUpdateUser = access?.update ?? true;

  // New state for search, filter, and UI enhancements
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, inactive
  const [roleFilter, setRoleFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  // Get unique roles for filter dropdown
  const uniqueRoles = useMemo(() => {
    const roles = [...new Set(state.userData.map((user) => user.authority))];
    return roles.filter((role) => role && role !== "-");
  }, [state.userData]);

  // Statistics calculations
  const stats = useMemo(() => {
    const total = state.userData.length;
    const active = state.userData.filter((user) => user.isEnable).length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [state.userData]);

  // Filtered data based on search and filters
  const filteredData = useMemo(() => {
    let data = [...state.userData];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      data = data.filter(
        (user) =>
          user.name?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          user.authority?.toLowerCase().includes(query) ||
          user.stations?.some((s) => s.toLowerCase().includes(query))
      );
    }

    // Apply status filter
    if (statusFilter === "active") {
      data = data.filter((user) => user.isEnable);
    } else if (statusFilter === "inactive") {
      data = data.filter((user) => !user.isEnable);
    }

    // Apply role filter
    if (roleFilter) {
      data = data.filter((user) => user.authority === roleFilter);
    }

    return data;
  }, [state.userData, searchQuery, statusFilter, roleFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setRoleFilter("");
  };
  useEffect(() => {
    const controller = new AbortController();
    if (customerIdList.length > 0) {
      GetAllUser(controller.signal);
    }
    return () => {
      controller.abort();
    };
  }, [customerIdList.length]);
  const GetAllUser = (signal) => {
    setState((state) => {
      state.loader = true;
    });
    const params = { customerIds: customerIdList.toString(), roleIdList: MASTER_ROLE_IDS.toString() };
    getAllUsers({ params, signal })
      .then((res) => {
        if (res?.data?.data) {
          const data = [];

          res?.data?.data?.forEach(
            ({ 
              id,
              firstName,
              lastName,
              email,
              authority,
              updatedTime,
              permissions,
              mobileNo,
              roleAdditionalInfo,
              isEnable,
              softwareResponses
            }) => {
              const zone_list = [];
              let stations = [];
              let div = [];
              permissions?.forEach((zone) => {
                if (Object.keys(zone).length > 0) {
                  const zones = zone;
                  const _zone = Object.keys(zones)[0];
                  zone_list.push(_zone);
                  let divisions = Object.values(zones);

                  divisions.forEach((data) => {
                    data.forEach((dt) => {
                      Object.entries(dt).forEach(([key, value]) => {
                        div.push(key);
                        value.forEach((v) => {
                          stations.push(v);
                        });
                      });
                    });
                  });
                }
              });
              if (id === USER_ID) {
                newUserData.customerDetails = stations;
                localStorage.setItem("userData", JSON.stringify(newUserData));
                setUserStationsData(permissions);
                // console.log()
              }
              stations = stations.map(({ customerName }) => customerName);
              // if (authority !== "admin") {
              let row = {
                id: id ?? "-",
                firstName: firstName ?? "-",
                lastName: lastName ?? "-",
                name: firstName + " " + lastName ?? "-",
                email: email ?? "-",
                authority: authority ?? "-",
                stations: stations ?? [],
                createdTime: updatedTime,
                permissions: permissions?.[0],
                mobileNumber: mobileNo,
                divisions: div ?? [],
                zones: zone_list ?? [],
                isEnable: isEnable ?? false,
                "G/NG": roleAdditionalInfo?.["G/NG"] ?? "-",
                softwareResponses: softwareResponses ?? [],
              };

              data.push(row);
              // }
            },
          );
          setState((previousState) => {
            previousState.userData = data;
            previousState.loader = false;
          });
        }
      })
      .catch((err) => {
        setState((previousState) => {
          previousState.loader = false;
        });
      });
  };

  const deleteUser = () => {
    const url = `${APP_BASE_URL}${DELETE_USER}${state.id}`;
    axios
      .delete(url)
      .then(() => {
        setState((previousState) => {
          previousState.showDeleteModal = false;
          previousState.snackbar = true;
          previousState.severityType = "success";
          previousState.snackbarMessage = "User has been deleted";
        });
        GetAllUser();
      })
      .catch(() => {
        setState((previousState) => {
          previousState.showDeleteModal = false;
          previousState.snackbar = true;
          previousState.severityType = "error";
          previousState.snackbarMessage = "Error Deleting User";
        });
      });
  };

  const deactivateUser = () => {
    const url = `${APP_BASE_URL}${ACTIVATE_DEACTIVATE_USER}`;
    const params = {
      status: !state.selectedRow?.isEnable,
      userId: state.selectedRow?.id,
    };
    axios
      .put(url, {}, { params })
      .then(() => {
        setState((previousState) => {
          previousState.showDeactivateModal = false;
          previousState.snackbar = true;
          previousState.severityType = "success";
          previousState.snackbarMessage = state.selectedRow?.isEnable
            ? "User has been deactivated"
            : "User has been activated";
          previousState.selectedRow = null;
        });
        GetAllUser();
      })
      .catch((e) => {
        if (axios.isAxiosError(e)) {
          toast.error(e?.response?.data?.error?.message || "Something went wrong! Try again later!");
          return;
        }
        toast.error(e?.message || "Something went wrong! Try again later!");
      });
  };

  const handleCloseUserModal = () => {
    setState((previousState) => {
      previousState.showUserModal = false;
      previousState.showDeleteModal = false;
      previousState.selectedRow = null;
      previousState.userActionLabel = "Create User";
      previousState.showDeactivateModal = false;
    });
  };

  const userColumn = useMemo(() => USERS_COLUMNS(setState, allowUpdateUser), []);
  const [pageSize, setPageSize] = useState(20);
  const handlePageSizeChange = useCallback((size) => setPageSize(size));
  const tableSize = useMemo(() => pageSize, [pageSize]);

  // Check if any filters are active
  const hasActiveFilters = searchQuery || statusFilter !== "all" || roleFilter;

  return (
    <div className="users">
      {/* Statistics Cards */}
      <Box className="users__stats" sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <StatCard
          title="Total Users"
          value={stats.total}
          icon={<GroupIcon fontSize="large" />}
          color="#1976d2"
          onClick={() => setStatusFilter("all")}
          isActive={statusFilter === "all"}
        />
        <StatCard
          title="Active Users"
          value={stats.active}
          icon={<PersonIcon fontSize="large" />}
          color="#4caf50"
          onClick={() => setStatusFilter("active")}
          isActive={statusFilter === "active"}
        />
        <StatCard
          title="Inactive Users"
          value={stats.inactive}
          icon={<PersonOffIcon fontSize="large" />}
          color="#f44336"
          onClick={() => setStatusFilter("inactive")}
          isActive={statusFilter === "inactive"}
        />
      </Box>

      {/* Search and Actions Bar */}
      <Box className="users__toolbar" sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search users by name, email, role, or station..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 300, flex: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Filter Toggle Button */}
        <Tooltip title="Toggle Filters">
          <Button
            variant={showFilters ? "contained" : "outlined"}
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            Filters
            {hasActiveFilters && (
              <Chip size="small" label="Active" color="primary" sx={{ ml: 1, height: 20 }} />
            )}
          </Button>
        </Tooltip>

        {/* Refresh Button */}
        <Tooltip title="Refresh Users">
          <IconButton onClick={() => GetAllUser()} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        {/* Export Button */}
        <Tooltip title="Export Users">
          <IconButton
            onClick={(e) => setExportAnchorEl(e.currentTarget)}
            color="primary"
          >
            <FileDownloadIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={exportAnchorEl}
          open={Boolean(exportAnchorEl)}
          onClose={() => setExportAnchorEl(null)}
        >
          <MenuItem
            onClick={() => {
              exportToCSV(filteredData, "users_filtered.csv");
              setExportAnchorEl(null);
            }}
          >
            Export Filtered ({filteredData.length} users)
          </MenuItem>
          <MenuItem
            onClick={() => {
              exportToCSV(state.userData, "users_all.csv");
              setExportAnchorEl(null);
            }}
          >
            Export All ({state.userData.length} users)
          </MenuItem>
        </Menu>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
          {allowCreateUser && (
            <Button variant="contained" onClick={() => addUser(setState)}>
              Create User
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate("/geofencing")}>
            GeoFencing
          </Button>
        </Box>
      </Box>

      {/* Collapsible Filters */}
      <Collapse in={showFilters}>
        <Box
          className="users__filters"
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            p: 2,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={roleFilter}
              label="Role"
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="">All Roles</MenuItem>
              {uniqueRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {hasActiveFilters && (
            <Button variant="text" color="secondary" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
            Showing {filteredData.length} of {state.userData.length} users
          </Typography>
        </Box>
      </Collapse>

      {/* Data Table */}
      <DataTable
        tableInfo={{
          id: `user-table`,
          label: `User`,
          requireAuthSign: false,
          timestamp: { startTs: null, endTs: null },
        }}
        pageSize={tableSize}
        pageSizeChange={handlePageSizeChange}
        loading={state.loader}
        rows={filteredData}
        columns={userColumn}
      />

      {state.showUserModal && (
        <AddUser
          users={state.userData ?? []}
          open={state.showUserModal}
          label={state.userActionLabel}
          onClose={handleCloseUserModal}
          editRow={state.selectedRow}
          userStationsData={userStationsData}
          update={(type, severity) => {
            GetAllUser();
            setState((previousState) => {
              previousState.snackbar = true;
              previousState.severityType = severity;
              previousState.snackbarMessage = type;
            });
          }}
        />
      )}
      {state.showDeleteModal && (
        <DeleteModal
          open={state.showDeleteModal}
          deleteMessageFor={"User"}
          onClose={handleCloseUserModal}
          willDelete={deleteUser}
        />
      )}
      {state.showDeactivateModal && (
        <ActivateDeactivateUser
          loading={state.loader}
          userActionLabel={state.selectedRow?.isEnable ? "Deactivate" : "Activate"}
          onClose={handleCloseUserModal}
          willAction={deactivateUser}
        />
      )}
      {state.snackbar && (
        <Snackbar
          open={state.snackbar}
          autoHideDuration={6000}
          onClose={() => {
            setState((draft) => {
              draft.snackbar = false;
            });
          }}
        >
          <Alert
            onClose={() => {
              setState((draft) => {
                draft.snackbar = false;
              });
            }}
            severity={state.severityType}
            className="fullWidth"
          >
            {state.snackbarMessage}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};

export default User;
